import { useCallback } from 'react';
import { useMutation, graphql } from 'react-relay';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { QueryError } from '@atlassian/jira-business-error-handling/src/utils/query-error/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { useFlagsService, type FlagConfiguration } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import { NAVIGATION_ITEMS_PAGE_SIZE } from '@atlassian/jira-navigation-apps-resources/src/constants.tsx';
import { getAnalyticsTabOrder } from '@atlassian/jira-horizontal-nav-common/src/common/utils.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { useRankNavigationItemMutation } from '@atlassian/jira-relay/src/__generated__/useRankNavigationItemMutation.graphql';
import RelayDataID from '@atlassian/relay-data-id';
import messages from './messages.tsx';

interface RankNavigationItemConfig {
	sourceTabIndex: number;
	placement: Placement;
	targetTabIndex: number;
	beforeItemId?: string | null;
	afterItemId?: string | null;
	id: string;
	dataId?: string;
	actionType: 'dragged' | 'clicked';
	containerNavigationId: string;
	scopeId: string;
}

const BEFORE = 'before';
const AFTER = 'after';
type Placement = typeof BEFORE | typeof AFTER;

const HANDLED_STATUS_CODES = [401, 403, 404];

const ERROR_TYPE = 'error';

const RANK_NAVIGATION_ERROR_FLAG: FlagConfiguration = {
	type: ERROR_TYPE,
	title: messages.errorTitle,
	description: messages.errorMessage,
	messageId: 'horizontal-nav-rank-item-service.services.rank-navigation-error-flag.flag.error',
	messageType: 'transactional',
};

export const PACKAGE_NAME = 'jiraNavigationKitRankItem';
export const REORDER_VIEW = 'reorderView';
export const TEAM_NAME = 'Navigation';

export const REORDER_VIEW_EXPERIENCE = { experience: REORDER_VIEW };

const getFlagDetails = (error: MutationError | QueryError): FlagConfiguration | null => {
	const statusCode =
		error instanceof MutationError
			? (error.extensions?.statusCode ?? null)
			: (error.extensions?.[0]?.statusCode ?? null);

	switch (statusCode) {
		case 401:
			return {
				type: ERROR_TYPE,
				title: messages.unauthenticatedUserErrorTitle,
				description: messages.unauthenticatedUserErrorMessage,
				messageId: 'horizontal-nav-rank-item-service.services.flag.unauthenticated-user-error',
				messageType: 'transactional',
			};
		case 403:
			return {
				type: ERROR_TYPE,
				title: messages.authorisationErrorTitle,
				description: messages.authorisationErrorMessage,
				messageId: 'horizontal-nav-rank-item-service.services.flag.authorisation-error',
				messageType: 'transactional',
			};
		case 404:
			return {
				type: ERROR_TYPE,
				title: messages.viewNotFoundErrorTitle,
				description: messages.viewNotFoundErrorMessage,
				messageId: 'horizontal-nav-rank-item-service.services.flag.view-not-found-error',
				messageType: 'transactional',
			};
		default: {
			return null;
		}
	}
};

export const useRankNavigationItem = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();

	const [commitMutation, isLoading] = useMutation<useRankNavigationItemMutation>(graphql`
		mutation useRankNavigationItemMutation($input: JiraRankNavigationItemInput!, $first: Int!) {
			jira {
				rankNavigationItem(input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
							errorType
						}
					}
					containerNavigation {
						...ContainerNavigationTabs_container
						... on JiraContainerNavigation {
							id
						}
						... on QueryError {
							__typename
							# eslint-disable-next-line @atlassian/relay/unused-fields
							message
							extensions {
								statusCode
							}
						}
					}
				}
			}
		}
	`);

	const onExperienceSuccess = useExperienceSuccess(REORDER_VIEW_EXPERIENCE);
	const onExperienceFail = useExperienceFail(REORDER_VIEW_EXPERIENCE);

	const sendErrorAnalytics = useCallback(
		(error: MutationError | Error) => {
			onExperienceFail(PACKAGE_NAME, error);
			fireErrorAnalytics({
				meta: {
					id: REORDER_VIEW,
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		[onExperienceFail],
	);

	const rankNavigationItem = useCallback(
		({
			placement,
			sourceTabIndex,
			targetTabIndex,
			id,
			beforeItemId,
			afterItemId,
			dataId,
			actionType,
			containerNavigationId,
			scopeId,
		}: RankNavigationItemConfig) => {
			let initialTabOrder: {
				itemType: string;
				itemId: string | null | undefined;
				defaultTab: boolean;
			}[] = [];
			commitMutation({
				variables: {
					input: {
						id,
						beforeItemId,
						afterItemId,
						scopeId,
					},
					first: NAVIGATION_ITEMS_PAGE_SIZE,
				},
				optimisticUpdater: (store) => {
					const relayId = RelayDataID({ id: containerNavigationId }, 'JiraContainerNavigation');
					const connection =
						relayId &&
						store
							.get(relayId)
							?.getLinkedRecord('navigationItems', { first: NAVIGATION_ITEMS_PAGE_SIZE });

					if (!connection) return;

					const existingEdges = connection?.getLinkedRecords('edges') || [];
					initialTabOrder = getAnalyticsTabOrder(store, containerNavigationId);
					const tabs = Array.from(existingEdges || []);

					const reorderedTabs = reorderWithEdge({
						list: tabs,
						startIndex: sourceTabIndex,
						indexOfTarget: targetTabIndex,
						closestEdgeOfTarget: placement === BEFORE ? 'left' : 'right',
						axis: 'horizontal',
					});

					const edges = reorderedTabs
						.map((tab) =>
							existingEdges.find(
								(edge) =>
									tab?.getLinkedRecord('node')?.getDataID() ===
									edge.getLinkedRecord('node')?.getDataID(),
							),
						)
						.filter(Boolean);

					connection?.setLinkedRecords(edges, 'edges');
				},
				updater: (store, response) => {
					if (!response?.jira?.rankNavigationItem?.success) return;

					const finalTabOrder = getAnalyticsTabOrder(store, containerNavigationId);
					const itemType = dataId && store.get(dataId)?.getValue('typeKey');
					const itemId = dataId && store.get(dataId)?.getValue('appId');

					let finalTabIndex = targetTabIndex;
					if (placement === AFTER && sourceTabIndex > targetTabIndex) {
						// placing it after the tab which will keep the same index, hence the destination is the target + 1
						finalTabIndex += 1;
					} else if (placement === BEFORE && sourceTabIndex < targetTabIndex) {
						// dropping it before the tab which will keep the same index, hence the destination is the target - 1
						finalTabIndex -= 1;
					}

					fireTrackAnalytics(createAnalyticsEvent({}), 'projectNavigationTabItem updated', {
						originalItems: initialTabOrder,
						updatedItems: finalTabOrder,
						itemType: String(itemType).toLowerCase(),
						itemId: itemId && String(itemId),
						action: 'reordered',
						method: actionType,
						initialTabIndex: sourceTabIndex,
						finalTabIndex,
					});
				},
				onCompleted: (data) => {
					const { errors, success, containerNavigation } = data?.jira?.rankNavigationItem ?? {};
					if (success && !errors) {
						onExperienceSuccess();
						return;
					}
					if (success && !errors) {
						onExperienceSuccess();
						return;
					}

					if (
						(errors && errors?.length > 0) ||
						(containerNavigation?.__typename && containerNavigation.__typename === 'QueryError')
					) {
						const { ...queryError } = containerNavigation ?? {};
						const error = errors ? new MutationError(errors[0]) : new QueryError(queryError);

						const flagDetails = getFlagDetails(error) ?? RANK_NAVIGATION_ERROR_FLAG;
						showFlag(flagDetails);

						if (HANDLED_STATUS_CODES.includes(error.statusCode ?? -1)) {
							onExperienceSuccess();
							return;
						}

						sendErrorAnalytics(error);
						return;
					}

					showFlag(RANK_NAVIGATION_ERROR_FLAG);
				},
				onError: (error) => {
					const err = new MutationError(error);
					showFlag(RANK_NAVIGATION_ERROR_FLAG);
					sendErrorAnalytics(err);
				},
			});
		},
		[commitMutation, createAnalyticsEvent, showFlag, onExperienceSuccess, sendErrorAnalytics],
	);

	return [rankNavigationItem, isLoading] as const;
};
